// Captura durável do handoff do VN2R-FireBIM.
//
// O FireBIM corre em localhost e nao pode expor o IFC por URL https, por isso
// envia os bytes (IFC + BCF opcional + GlobalId) por window.postMessage para a
// aba do hub que abriu. O problema: se a aba abre na pagina de login (o
// utilizador ainda nao esta autenticado), o viewer e o seu receiver ainda nao
// estao montados, e a mensagem perder-se-ia.
//
// Solucao: instalamos um listener GLOBAL logo no arranque (main.tsx, antes e
// independentemente do gate de autenticacao) que:
//   1) valida a forma da mensagem,
//   2) grava o payload em IndexedDB. Isto e durravel: sobrevive ao reload que o
//      magic-link possa causar e e partilhado entre abas da mesma origem (se o
//      login abrir noutra aba, o viewer dessa aba drena na mesma),
//   3) avisa o remetente que a aba esta viva (ready ping para window.opener),
//   4) dispara um CustomEvent para um viewer ja montado drenar de imediato.
// Quando o viewer monta (depois do login), drena o IndexedDB e carrega tudo.
//
// Nota de confianca: validamos a forma (type + schema + ifc e ArrayBuffer) e
// a ORIGEM. O remetente corre em localhost com portas variaveis, por isso a
// origem aceite e qualquer porta de loopback (localhost, 127.0.0.1, [::1]),
// alem da propria origem do hub. Sem esta verificacao, qualquer site que
// abrisse o hub numa janela (window.open) podia mandar-lhe um IFC e um BCF:
// o payload substitui o modelo carregado e importa topicos na sessao
// autenticada de quem estava a trabalhar.

const DB_NAME = 'vn2r-firebim-handoff'
const STORE = 'payload'
const KEY = 'pending'
const OPEN_TYPE = 'vn2r-firebim-open'
const READY_TYPE = 'vn2r-firebim-hub-ready'

/** Evento de janela emitido depois de um payload ser gravado, para um viewer
 *  ja montado drenar sem esperar pelo proximo ciclo. */
export const HANDOFF_STORED_EVENT = 'vn2r-firebim:handoff-stored'

/** Payloads mais antigos que isto sao ignorados, para nao carregar um handoff
 *  esquecido muito tempo depois (30 minutos). */
const MAX_AGE_MS = 30 * 60 * 1000

export interface HandoffPayload {
  ifc: ArrayBuffer
  ifcName?: string
  bcf?: ArrayBuffer
  bcfName?: string
  globalId?: string
  /** GlobalIds a seleccionar e enquadrar. Normalmente [globalId]; para um
   *  compartimento (IfcZone, sem geometria) sao os espacos membros. */
  focusIds?: string[]
  /** Epoch ms de quando foi recebido (para a verificacao de validade). */
  ts: number
}

interface OpenMessage {
  type: typeof OPEN_TYPE
  schema: 1
  ifc: ArrayBuffer
  ifcName?: string
  bcf?: ArrayBuffer
  bcfName?: string
  globalId?: string
  focusIds?: string[]
}

/** A mensagem vem de quem pode mandar um handoff? Loopback em qualquer
 *  porta (o FireBIM local) ou a propria origem do hub. */
export function isTrustedHandoffOrigin(origin: string, selfOrigin: string): boolean {
  if (!origin || origin === 'null') return false
  if (origin === selfOrigin) return true
  let u: URL
  try {
    u = new URL(origin)
  } catch {
    return false
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return false
  const host = u.hostname
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]'
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string')
}

function isOpenMessage(data: unknown): data is OpenMessage {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  return (
    d.type === OPEN_TYPE &&
    d.schema === 1 &&
    d.ifc instanceof ArrayBuffer &&
    (d.bcf === undefined || d.bcf instanceof ArrayBuffer) &&
    (d.focusIds === undefined || isStringArray(d.focusIds))
  )
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function putPayload(payload: HandoffPayload): Promise<void> {
  return openDb().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite')
        tx.objectStore(STORE).put(payload, KEY)
        tx.oncomplete = () => {
          db.close()
          resolve()
        }
        tx.onerror = () => {
          db.close()
          reject(tx.error)
        }
        tx.onabort = () => {
          db.close()
          reject(tx.error)
        }
      }),
  )
}

/** Le o payload pendente sem o apagar. Devolve null se nao houver ou se
 *  estiver fora de validade (que tambem o limpa). */
export async function peekPendingHandoff(): Promise<HandoffPayload | null> {
  const db = await openDb()
  const payload = await new Promise<HandoffPayload | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(KEY)
    req.onsuccess = () => resolve(req.result as HandoffPayload | undefined)
    req.onerror = () => reject(req.error)
  })
  db.close()
  if (!payload) return null
  if (typeof payload.ts === 'number' && Date.now() - payload.ts > MAX_AGE_MS) {
    await clearPendingHandoff()
    return null
  }
  return payload
}

export async function clearPendingHandoff(): Promise<void> {
  const db = await openDb()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).delete(KEY)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
  db.close()
}

function pingOpenerReady(): void {
  if (!window.opener) return
  try {
    window.opener.postMessage({ type: READY_TYPE, schema: 1 }, '*')
  } catch (err) {
    console.warn('[firebim-handoff] ready ping failed', err)
  }
}

let installed = false

/**
 * Instala a captura global do handoff. Idempotente. Chamar no arranque
 * (main.tsx), antes de qualquer gate de autenticacao, para apanhar payloads
 * que cheguem enquanto a app ainda esta no login.
 */
export function installFirebimHandoffCapture(): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  // Avisa o remetente que a aba esta viva e pode enviar, ja (mesmo no login):
  // os bytes que cheguem sao persistidos.
  pingOpenerReady()

  window.addEventListener('message', (event: MessageEvent) => {
    if (!isOpenMessage(event.data)) return
    if (!isTrustedHandoffOrigin(event.origin, window.location.origin)) {
      console.warn('[firebim-handoff] mensagem ignorada: origem nao autorizada', event.origin)
      return
    }
    const d = event.data
    const payload: HandoffPayload = {
      ifc: d.ifc,
      ifcName: d.ifcName,
      bcf: d.bcf,
      bcfName: d.bcfName,
      globalId: d.globalId,
      focusIds: d.focusIds,
      ts: Date.now(),
    }
    putPayload(payload)
      .then(() => window.dispatchEvent(new CustomEvent(HANDOFF_STORED_EVENT)))
      .catch((err) => console.error('[firebim-handoff] falha a gravar o payload', err))
  })
}
